'use client';

import { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { SvgSpinner } from '@icons/oldIcons/SvgSpinner';
import { Product, ProductType } from '@interface/product';
import { HOLAGLOW_COLORS } from '@utils/colors';
import { setSeoMetaData } from '@utils/common';
import { filterItems } from '@utils/filterItems';
import AnimateOnViewport from 'app/(web)/components/common/AnimateOnViewport';
import CategorySelector from 'app/(web)/components/filters/CategorySelector';
import ProductCard from 'app/(web)/components/product/ProductCard';
import ProductSearchBar from 'app/(web)/components/product/ProductSearchBar';
import { useGlobalStore } from 'app/stores/globalStore';
import { Container, Flex } from 'designSystem/Layouts/Layouts';
import { isEmpty } from 'lodash';

import { applyFilters } from '../utils/filters';
import PVBanner from './PVBanner';

export default function PsrpProductList({
  products,
  slug = '',
  type,
}: {
  products: Product[];
  slug?: string;
  type: ProductType;
}) {
  const {
    filteredProducts,
    setFilteredProducts,
    productFilters,
    setProductFilters,
  } = useGlobalStore(state => state);

  const [isHydrated, setIsHydrated] = useState(true);

  const metadataPacks = {
    title: 'Packs de tratamientos de medicina estética - Holaglow',
    description:
      'Elige uno de los packs para tratar de manera global tus objetivos estéticos y conseguir el resultado que deseas',
  };

  useEffect(() => {
    if (slug !== '') {
      let filterToApply = '';
      switch (slug) {
        case 'piel':
          filterToApply = 'Calidad de la Piel';
          break;
        case 'pelo':
          filterToApply = 'Caida del pelo';
          break;
        default:
          filterToApply = slug[0].toUpperCase() + slug.substr(1).toLowerCase();
          break;
      }
      const categoryExists = filterItems.some(x => {
        const exists = x.buttons.some(y => {
          if (y.value == filterToApply) return true;
        });
        return exists;
      });

      if (
        filterToApply &&
        productFilters.category.indexOf(filterToApply) == -1 &&
        categoryExists
      ) {
        if (filterToApply == 'Calidad de la Piel')
          filterToApply = 'Calidad Piel';
        productFilters.category.push(filterToApply);
      }

      if (slug === 'packs') {
        setSeoMetaData(metadataPacks.title, metadataPacks.description);
      }

      setProductFilters(productFilters);
      processFilters();
    }

    if (type) {
      setProductFilters({
        ...productFilters,
        type: [type],
      });
    }
  }, [slug, type, products]);

  useEffect(() => {
    processFilters();
  }, [productFilters]);

  useEffect(() => {
    if (filteredProducts.length > 0) {
      setIsHydrated(true);
    }
  }, [filteredProducts]);

  useEffect(() => {
    if (isHydrated && productFilters.type.length > 0) {
      processFilters();
    }
  }, [isHydrated]);

  function processFilters() {
    if (isEmpty(filteredProducts)) {
      setFilteredProducts(products);
      setFilteredProducts(
        applyFilters({ products: products, filters: productFilters })
      );
    } else {
      setFilteredProducts(
        applyFilters({ products: filteredProducts, filters: productFilters })
      );
    }
  }

  if (!isHydrated && filteredProducts.length > 0) {
    return <></>;
  }

  return (
    <>
      {isEmpty(filteredProducts) && (
        <Flex layout="row-left" className="justify-center">
          <SvgSpinner
            fill={HOLAGLOW_COLORS['secondary']}
            height={50}
            width={50}
          />
        </Flex>
      )}

      {!isEmpty(filteredProducts) && (
        <div className="bg-hg-cream300 pb-32 relative">
          <Container className="md:hidden pt-4">
            {isMobile && (
              <ProductSearchBar
                products={products}
                className="mb-4"
                isMobileNavigation={isMobile}
              />
            )}
          </Container>
          <Container className="px-0 md:px-4 md:pt-8 mb-8 relative">
            <div className="xl:flex xl:flex-row xl:justify-start items-center">
              {!isMobile && (
                <ProductSearchBar
                  products={products}
                  isMobileNavigation={isMobile}
                  className="hidden md:block mr-4 lg:max-w-[262px] md:mb-4 xl:mb-0"
                />
              )}
              <AnimateOnViewport
                origin={isMobile ? 'right' : 'bottom'}
                className="shrink-0 mb-4 md:mb-0"
              >
                <CategorySelector type={type} products={products} />
              </AnimateOnViewport>
            </div>
          </Container>

          <Container className="px-8 md:px-4">
            <ul className="transition-all grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 flex-col gap-8 md:gap-6 md:pt-0 pb-6">
              <li key="pvBanner">
                <PVBanner
                  type={
                    type === ProductType.Medical
                      ? 'pv'
                      : type === ProductType.Esthetic
                        ? 'asesoramientoPiel'
                        : 'asesoramientoLongevidad'
                  }
                />
              </li>

              {filteredProducts
                .filter(product => product.visibility)
                .map(product => {
                  return (
                    <li key={product.id}>
                      <ProductCard
                        product={product}
                        className="h-full flex flex-col"
                      />
                    </li>
                  );
                })}
            </ul>
          </Container>
        </div>
      )}
    </>
  );
}
